import * as React from 'react';
import { Navigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import AuthService from '../services/auth';
import OrgLogo from '../components/OrgLogo';
import AppConfig from '../services/app-config';
import wsClient from '../services/ws';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';

import HistoryIcon from '@mui/icons-material/History';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DeskIcon from '@mui/icons-material/Desk';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ChairRoundedIcon from '@mui/icons-material/ChairRounded';
import SettingsIcon from '@mui/icons-material/Settings';
import EventIcon from '@mui/icons-material/Event';
import apiClient, { shouldRedirectToLogin } from '../services/api-client';
import BannerNotificationsStack from '../components/BannerNotificationsStack';
import WeatherModal from '../components/WeatherModal';
import LocationModal from '../components/LocationModal';

export default function AdminHub({ setNavbarState, navbarState }) {
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [org, setOrg] = React.useState("");
  const [weather, setWeather] = React.useState({ temp: "", icon: 'NONE', fetched: false });
  const [status, setStatus] = React.useState({
    visitors: 0,
    pending_issues: 0,
    pending_approvals: 0,
    activity_entries: 0,
  });
  const [connectedUsers, setConnectedUsers] = React.useState(0);
  const [bannerNotifications, setBannerNotifications] = React.useState([]);
  const [editingQuickActions, setEditingQuickActions] = React.useState(false);
  const [quickActionOrder, setQuickActionOrder] = React.useState([]);
  const [weatherModalOpen, setWeatherModalOpen] = React.useState(false);
  const [locationModalOpen, setLocationModalOpen] = React.useState(false);
  const dragActionIdRef = React.useRef(null);

  const getWeather = async () => {
    try {
      const response = await apiClient.get('weather/current', {}, { action: 'fetch_weather' });
      setWeather({ temp: response.data.data.temp, icon: response.data.data.icon, fetched: true });
    } catch (error) {
      console.error(error);
    }
  };

  const persistQuickActionOrder = React.useCallback((order) => {
    try {
      localStorage.setItem('adminhub_quick_actions_order_v1', JSON.stringify(order));
    } catch (e) {
      // ignore
    }
  }, []);

  const buildQuickActionDefs = React.useCallback(() => {
    const items = [
      {
        id: 'usuarios',
        label: 'Usuarios',
        route: 'useradmin',
        icon: <AdminPanelSettingsIcon sx={{ color: '#238048', fontSize: '1.75rem' }} />,
        badgeContent: connectedUsers,
        badgeColor: undefined,
        badgeInvisible: connectedUsers <= 0,
      },
    ];

    if (AppConfig.haveModule('visits')) {
      items.push({
        id: 'recepcion',
        label: 'Recepción',
        route: 'recepcion',
        icon: <DeskIcon sx={{ color: '#238048', fontSize: '1.75rem' }} />,
        badgeContent: status.visitors,
        badgeColor: 'info',
      });
    }

    if (AppConfig.haveModule('issues')) {
      items.push({
        id: 'reportes',
        label: 'Reportes',
        route: 'reportes',
        icon: <ListAltIcon sx={{ color: '#238048', fontSize: '1.75rem' }} />,
        badgeContent: status.pending_issues,
        badgeColor: 'error',
      });
    }

    if (AppConfig.haveModule('amenities')) {
      items.push({
        id: 'reservas',
        label: 'Reservas',
        route: 'reservaciones',
        icon: <ChairRoundedIcon sx={{ color: '#238048', fontSize: '1.75rem' }} />,
        badgeContent: status.pending_approvals,
        badgeColor: 'info',
      });
      items.push({
        id: 'aprobaciones',
        label: 'Aprobaciones',
        route: 'aprobaciones',
        icon: <FactCheckOutlinedIcon sx={{ color: '#238048', fontSize: '1.75rem' }} />,
        badgeContent: status.pending_approvals,
        badgeColor: 'error',
      });
    }

    return items;
  }, [connectedUsers, status.pending_approvals, status.pending_issues, status.visitors]);

  const DEFAULT_QUICK_ACTION_ORDER = React.useMemo(
    () => ['usuarios', 'recepcion', 'reportes', 'reservas', 'aprobaciones'],
    []
  );

  const quickActions = React.useMemo(() => {
    const defs = buildQuickActionDefs();
    const byId = new Map(defs.map((d) => [d.id, d]));
    const baseOrder = Array.isArray(quickActionOrder) && quickActionOrder.length > 0
      ? quickActionOrder
      : DEFAULT_QUICK_ACTION_ORDER;
    const ordered = [];

    for (const id of baseOrder) {
      const item = byId.get(id);
      if (item) {
        ordered.push(item);
        byId.delete(id);
      }
    }

    for (const item of byId.values()) {
      ordered.push(item);
    }

    return ordered;
  }, [buildQuickActionDefs, DEFAULT_QUICK_ACTION_ORDER, quickActionOrder]);

  React.useEffect(() => {
    const baseOrder = Array.isArray(quickActionOrder) && quickActionOrder.length > 0
      ? quickActionOrder
      : DEFAULT_QUICK_ACTION_ORDER;
    const currentIds = new Set(buildQuickActionDefs().map((d) => d.id));
    const merged = [
      ...baseOrder.filter((id) => currentIds.has(id)),
      ...[...currentIds].filter((id) => !baseOrder.includes(id)),
    ];

    if (JSON.stringify(merged) !== JSON.stringify(baseOrder)) {
      setQuickActionOrder(merged);
      persistQuickActionOrder(merged);
    }
  }, [DEFAULT_QUICK_ACTION_ORDER, buildQuickActionDefs, persistQuickActionOrder, quickActionOrder]);

  const onQuickActionDragStart = React.useCallback((actionId) => {
    dragActionIdRef.current = actionId;
  }, []);

  const onQuickActionDrop = React.useCallback((overActionId) => {
    const draggedId = dragActionIdRef.current;
    dragActionIdRef.current = null;

    if (!draggedId || !overActionId || draggedId === overActionId) {
      return;
    }

    setQuickActionOrder((prev) => {
      const base = Array.isArray(prev) && prev.length > 0 ? [...prev] : [...DEFAULT_QUICK_ACTION_ORDER];
      const fromIdx = base.indexOf(draggedId);
      const toIdx = base.indexOf(overActionId);

      if (fromIdx === -1 || toIdx === -1) {
        return base;
      }

      base.splice(fromIdx, 1);
      base.splice(toIdx, 0, draggedId);
      persistQuickActionOrder(base);
      return base;
    });
  }, [DEFAULT_QUICK_ACTION_ORDER, persistQuickActionOrder]);

  const showPendingSection = true;
  const showQuickActionsSection = true;
  const showAmenitiesSection = true;
  const showActivitySection = true;

  const getStatus = async () => {
    try {
      const response = await apiClient.get('admin/status', {}, { action: 'fetch_admin_status' });
      const data = response.data.data;
      setStatus(data);
      setBannerNotifications(data?.banner_notifications || []);
      const statusConnections = data?.connections ?? data?.connected_users;
      if (typeof statusConnections === 'number') {
        setConnectedUsers(statusConnections);
      }
    } catch (error) {
      if (shouldRedirectToLogin(error)) {
        setRedirect({ trigger: true, route: '/login' })
      }
    }
  };

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: false, showOptions: false, showSearch: false });
    setOrg(AuthService.getCurrentUserOrg());
    getWeather();
    getStatus();
    try {
      const stored = JSON.parse(localStorage.getItem('adminhub_quick_actions_order_v1') || '[]');
      if (Array.isArray(stored) && stored.length > 0) {
        setQuickActionOrder(stored);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  React.useEffect(() => {
    if (!org) {
      return;
    }

    const offConnectedUsers = wsClient.on('connected_users', (msg) => {
      const eventOrg = msg?.data?.org;
      const connections = msg?.data?.connections;
      const visitorsCount = msg?.data?.visitors;

      if (!eventOrg || eventOrg.toLowerCase() !== org.toLowerCase()) {
        return;
      }

      if (typeof connections === 'number') {
        setConnectedUsers(connections);
      }

      if (typeof visitorsCount === 'number') {
        setStatus((prev) => ({
          ...(prev || {}),
          visitors: visitorsCount,
        }));
      }
    });

    return () => {
      if (typeof offConnectedUsers === 'function') {
        offConnectedUsers();
      }
    };
  }, [org]);

  const handleClick = (route) => {
    setRedirect({ trigger: true, route: `/${route.toLowerCase()}` });
  };

  const markBannerAction = React.useCallback(async (notificationId, action) => {
    if (!notificationId) {
      return;
    }
    try {
      await apiClient.post('banner_notifications/action', {
        notification_id: notificationId,
        action,
      }, {}, { action: `admin_banner_${action}` });
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleDismissBanner = React.useCallback((notificationId) => {
    if (!notificationId) {
      return;
    }
    setBannerNotifications((prev) => prev.filter((banner) => banner.id !== notificationId));
    markBannerAction(notificationId, 'dismiss');
  }, [markBannerAction]);

  const handleViewBanner = React.useCallback((notificationId) => {
    markBannerAction(notificationId, 'view');
  }, [markBannerAction]);

  const navigateToBannerUrl = React.useCallback((url) => {
    if (!url) {
      return;
    }

    const trimmed = url.trim();

    if (!trimmed) {
      return;
    }

    const isAbsolute = /^https?:\/\//i.test(trimmed);

    if (isAbsolute) {
      window.open(trimmed, '_blank', 'noopener,noreferrer');
      return;
    }

    const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    setRedirect({ trigger: true, route: normalized });
  }, [setRedirect]);

  const getWeatherShortLabel = React.useCallback((icon) => {
    switch (String(icon || '').toUpperCase()) {
      case 'CLEAR_DAY':
        return 'Soleado';
      case 'PARTLY_CLOUDY_DAY':
        return 'Parcial';
      case 'CLOUDY':
        return 'Nublado';
      case 'RAIN':
        return 'Lluvia';
      case 'SLEET':
        return 'Aguanieve';
      case 'SNOW':
        return 'Nieve';
      case 'FOG':
        return 'Niebla';
      case 'CLEAR_NIGHT':
        return 'Despejado';
      case 'PARTLY_CLOUDY_NIGHT':
        return 'Parcial';
      default:
        return 'Clima';
    }
  }, []);

  const nowLabel = React.useMemo(() => {
    try {
      return new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return '';
    }
  }, []);

  const cardSx = {
    borderRadius: '10px',
    boxShadow: 0,
    border: '1px solid',
    borderColor: 'divider',
  };

  const quickActionCircleSx = {
    width: 56,
    height: 56,
    borderRadius: '999px',
    backgroundColor: 'rgba(35,128,72,0.10)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform 120ms ease',
    '&:active': {
      transform: 'scale(0.95)',
    },
  };

  if (!AuthService.userRoleIncludes(["admin"])) {
    return <Navigate to="/" replace={true} />;
  }

  return (
    <Container maxWidth="sm" disableGutters>
      {redirect.trigger && <Navigate to={redirect.route} replace={true} />}
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 10, transition: 'background-color 200ms ease' }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: 3,
            pt: 3,
            pb: 2,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
              <OrgLogo
                org={String(org || '').toLowerCase()}
                alt={`${org}-logo`}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '10px',
                  objectFit: 'contain',
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 0.5,
                }}
              />
              <Box>
                <Typography sx={{ fontSize: '0.625rem', fontWeight: 800, opacity: 0.65, letterSpacing: 1, textTransform: 'uppercase' }}>
                  Hola, {String(AuthService.getCurrentUser() || '').toUpperCase()}
                </Typography>
                <Typography sx={{ fontSize: '1.375rem', fontWeight: 900, lineHeight: 1.1, letterSpacing: -0.5, color: 'text.primary' }}>
                  Panel de Admin
                </Typography>
              </Box>
            </Box>

            <IconButton
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.primary',
              }}
              data-tour="adminhub-profile-link"
              onClick={() => handleClick('perfil')}
            >
              <PersonIcon />
            </IconButton>
          </Box>

          <Stack direction="row" spacing={1.25} sx={{ overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
            <Chip
              variant="outlined"
              icon={weather.fetched ? <WbSunnyOutlinedIcon /> : <CircularProgress size={16} />}
              label={weather.fetched ? `${getWeatherShortLabel(weather.icon)} ${weather.temp}°C` : 'Cargando clima'}
              onClick={() => setWeatherModalOpen(true)}
              sx={{
                borderRadius: '10px',
                borderColor: 'divider',
                color: 'text.primary',
                height: 36,
                flexShrink: 0,
                backgroundColor: 'background.paper',
                cursor: 'pointer',
                '& .MuiChip-icon': { color: 'text.primary' },
                '& .MuiChip-label': { fontWeight: 700, fontSize: '0.75rem' },
              }}
            />
            <Chip
              variant="outlined"
              icon={<LocationOnOutlinedIcon />}
              label={org || '—'}
              onClick={() => setLocationModalOpen(true)}
              sx={{
                borderRadius: '10px',
                borderColor: 'divider',
                color: 'text.primary',
                height: 36,
                flexShrink: 0,
                backgroundColor: 'background.paper',
                cursor: 'pointer',
                '& .MuiChip-icon': { color: 'text.primary' },
                '& .MuiChip-label': { fontWeight: 700, fontSize: '0.75rem' },
              }}
            />
            <Chip
              variant="outlined"
              icon={<CalendarTodayOutlinedIcon />}
              label={nowLabel || '—'}
              sx={{
                borderRadius: '10px',
                borderColor: 'divider',
                color: 'text.primary',
                height: 36,
                flexShrink: 0,
                backgroundColor: 'background.paper',
                '& .MuiChip-icon': { color: 'text.primary' },
                '& .MuiChip-label': { fontWeight: 700, fontSize: '0.75rem' },
              }}
            />
          </Stack>
        </Box>

        <Box sx={{ px: 3, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <BannerNotificationsStack
            banners={bannerNotifications}
            onNavigate={navigateToBannerUrl}
            onView={handleViewBanner}
            onDismiss={handleDismissBanner}
          />

          {showPendingSection && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 900, color: 'text.primary' }}>Atención Requerida</Typography>
              {(status.pending_issues > 0 || status.pending_approvals > 0) && (
                <Box sx={{ width: 8, height: 8, borderRadius: '999px', backgroundColor: '#c92c51' }} />
              )}
            </Box>

            <Stack spacing={1.5}>
              {AppConfig.haveModule('amenities') && status.pending_approvals > 0 && (
                <Card sx={{ ...cardSx, cursor: 'pointer' }} onClick={() => handleClick('reservaciones')}>
                  <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(201,44,81,0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <EventIcon sx={{ color: '#c92c51' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 900, color: 'text.primary' }}>
                            Aprobar Reservaciones
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                            {status.pending_approvals} {status.pending_approvals === 1 ? 'pendiente de aprobación' : 'pendientes de aprobación'}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick('aprobaciones');
                        }}
                        sx={{
                          height: 32,
                          px: 2,
                          borderRadius: '999px',
                          backgroundColor: '#c92c51',
                          color: 'white',
                          fontWeight: 900,
                          fontSize: '0.6875rem',
                          '&:hover': { backgroundColor: '#b32646' },
                        }}
                      >
                        Revisar
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {AppConfig.haveModule('issues') && status.pending_issues > 0 && (
                <Card sx={{ ...cardSx, cursor: 'pointer' }} onClick={() => handleClick('reportes')}>
                  <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(201,44,81,0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <ListAltIcon sx={{ color: '#c92c51' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 900, color: 'text.primary' }}>
                            Reportes Urgentes
                          </Typography>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                            {status.pending_issues} {status.pending_issues === 1 ? 'reporte pendiente' : 'reportes pendientes'} de revisar
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick('reportes');
                        }}
                        sx={{
                          height: 32,
                          px: 2,
                          borderRadius: '999px',
                          backgroundColor: '#c92c51',
                          color: 'white',
                          fontWeight: 900,
                          fontSize: '0.6875rem',
                          '&:hover': { backgroundColor: '#b32646' },
                        }}
                      >
                        Atender
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {(status.pending_issues <= 0 && status.pending_approvals <= 0) && (
                <Card sx={cardSx}>
                  <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 800, color: 'text.primary' }}>
                      Sin pendientes
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                      Todo está al día.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Box>
          )}

          {showQuickActionsSection && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 900, color: 'text.primary' }}>Acciones Rápidas</Typography>
              {editingQuickActions ? (
                <Button
                  onClick={() => setEditingQuickActions(false)}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 900,
                    textTransform: 'none',
                    p: 0,
                    minWidth: 0,
                  }}
                >
                  Listo
                </Button>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => setEditingQuickActions(true)}
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: 'divider',
                    color: 'text.primary',
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {!editingQuickActions && (
              <Box sx={{ overflowX: 'auto', overflowY: 'visible', pb: 1.25, pt: 0.25 }} data-tour="adminhub-quick-actions">
                <Box sx={{ display: 'flex', gap: 2, width: 'max-content' }}>
                  {quickActions.map((action) => (
                    (() => {
                      const badgeValue = Number(action.badgeContent);
                      const hideBadge = Boolean(action.badgeInvisible) || !Number.isFinite(badgeValue) || badgeValue <= 0;

                      return (
                    <Box
                      key={action.id}
                      sx={{ textAlign: 'center', cursor: 'pointer', width: 72 }}
                      onClick={() => handleClick(action.route)}
                    >
                      <Badge
                        badgeContent={Number.isFinite(badgeValue) ? badgeValue : 0}
                        color={action.badgeColor}
                        max={99}
                        invisible={hideBadge}
                        sx={{
                          overflow: 'visible',
                          '& .MuiBadge-badge': action.id === 'usuarios' ? {
                            bgcolor: '#238048',
                            color: 'white',
                            fontWeight: 900,
                            top: 6,
                            right: 10,
                          } : {
                            top: 6,
                            right: 10,
                          },
                        }}
                      >
                        <Box sx={{ ...quickActionCircleSx, mx: 'auto' }}>
                          {action.icon}
                        </Box>
                      </Badge>
                      <Typography sx={{ mt: 1, fontSize: '0.75rem', fontWeight: 700, color: 'text.primary' }}>{action.label}</Typography>
                    </Box>
                      );
                    })()
                  ))}
                </Box>
              </Box>
            )}

            {editingQuickActions && (
              <Grid container spacing={2}>
                {quickActions.map((action) => (
                  (() => {
                    const badgeValue = Number(action.badgeContent);
                    const hideBadge = Boolean(action.badgeInvisible) || !Number.isFinite(badgeValue) || badgeValue <= 0;

                    return (
                  <Grid key={action.id} item xs={3}>
                    <Box
                      draggable
                      onDragStart={() => onQuickActionDragStart(action.id)}
                      onDragOver={(evt) => evt.preventDefault()}
                      onDrop={() => onQuickActionDrop(action.id)}
                      sx={{
                        textAlign: 'center',
                        cursor: 'grab',
                        '@keyframes quickActionWiggle': {
                          '0%': { transform: 'rotate(-1deg) translateY(0px)' },
                          '50%': { transform: 'rotate(1deg) translateY(-1px)' },
                          '100%': { transform: 'rotate(-1deg) translateY(0px)' },
                        },
                      }}
                    >
                      <Badge
                        badgeContent={Number.isFinite(badgeValue) ? badgeValue : 0}
                        color={action.badgeColor}
                        max={99}
                        invisible={hideBadge}
                        sx={{
                          overflow: 'visible',
                          '& .MuiBadge-badge': action.id === 'usuarios' ? {
                            bgcolor: '#238048',
                            color: 'white',
                            fontWeight: 900,
                            top: 6,
                            right: 10,
                          } : {
                            top: 6,
                            right: 10,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            ...quickActionCircleSx,
                            mx: 'auto',
                            animation: 'quickActionWiggle 0.18s infinite ease-in-out',
                          }}
                        >
                          {action.icon}
                        </Box>
                      </Badge>
                      <Typography sx={{ mt: 1, fontSize: '0.75rem', fontWeight: 700, color: 'text.primary' }}>{action.label}</Typography>
                    </Box>
                  </Grid>
                    );
                  })()
                ))}
              </Grid>
            )}
          </Box>
          )}

          {AppConfig.haveModule('amenities') && showAmenitiesSection && (
            <Box>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 900, color: 'text.primary', mb: 2 }}>Amenidades</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Card sx={{ ...cardSx, cursor: 'pointer' }} onClick={() => handleClick('amenidades')}>
                    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(35,128,72,0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <ChairRoundedIcon sx={{ color: '#238048' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 900, color: 'text.primary' }}>Ver amenidades</Typography>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Disponibles en {org || 'tu comunidad'}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={6}>
                  <Card sx={{ ...cardSx, cursor: 'pointer' }} onClick={() => handleClick('amenityadmin')}>
                    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(35,128,72,0.10)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <SettingsIcon sx={{ color: '#238048' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 900, color: 'text.primary' }}>Administrar</Typography>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Configurar amenidades</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {showActivitySection && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontSize: '1.125rem', fontWeight: 900, color: 'text.primary' }}>Actividad</Typography>
              <Button
                onClick={() => handleClick('actividad')}
                sx={{
                  p: 0,
                  minWidth: 0,
                  color: 'secondary.main',
                  fontWeight: 900,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                }}
              >
                Ver todo
              </Button>
            </Box>

            <Card sx={{ ...cardSx, cursor: 'pointer' }} onClick={() => handleClick('actividad')}>
              <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '999px',
                      backgroundColor: 'rgba(35,128,72,0.10)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <HistoryIcon sx={{ color: '#238048' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 900, color: 'text.primary' }}>
                      Actividad reciente
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                      {status.activity_entries > 0
                        ? `${status.activity_entries} ${status.activity_entries === 1 ? 'registro el día de hoy' : 'registros el día de hoy'}`
                        : 'Sin registros hoy'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
          )}
        </Box>

      </Box>
      <WeatherModal open={weatherModalOpen} onClose={() => setWeatherModalOpen(false)} />
      <LocationModal open={locationModalOpen} onClose={() => setLocationModalOpen(false)} />
    </Container>
  );
}